import React, { useState, useRef, useEffect } from "react";
import { useUpdateEffect } from "react-use";
import PropTypes from "prop-types";

// Layout/UI
import { Box, Tabs, Text } from "@metrea-ui/v2";
import ChooseOneValue from "src/components/UI/Preference/ChooseOneValue";
import ChooseTwoValues from "src/components/UI/Preference/ChooseTwoValues";

// Utilities
import { mColor } from "src/utils/themeUtils";
import { Session } from "src/utils/sessionUtils";
import { debugLog } from "src/utils/debugUtils";

const prefInstructions = {
	601: "Use these sliders to indicate how your strategy changes the balance of emphasis among modernization, force structure, and readiness relative to the current strategy. The green handles indicate the near-term preference (<5 years) and the blue handles indicate the long-term preference (>5 years). Zero means maintaining the current level of emphasis, +1 means increasing emphasis as much as possible, and -1 means decreasing emphasis as much as possible.",
	121: "Use these sliders to indicate how your strategy changes the emphasis for each of the types of operations shown. All changes are relative to the current strategy and the emphasis it places on each type of operations. The green handles indicate the near-term preference (<5 years) and the blue handles indicate the long-term preference (>5 years). Zero means maintaining the current level of emphasis, +1 means increasing emphasis as much as possible, and -1 means decreasing emphasis as much as possible.",
	122: "Use these sliders to indicate how your strategy changes the emphasis on each of the geographic regions shown above relative to the current strategy. The green handles indicate the near-term preference (<5 years) and the blue handles indicate the long-term preference (>5 years). Zero means maintaining the current level of emphasis, +1 means increasing emphasis as much as possible, and -1 means decreasing emphasis as much as possible.",
	611: "Use these sliders to indicate how much you want to increase or decrease the budget for each year relative to the baseline level of funding. For example, if the baseline level of funding for a given year is $700B and you would like to raise that to $750B, then move the slider to +$50B.",
};

function PreferenceTabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box p={2}>
					{children}
				</Box>
			)}
		</div>
	);
}

PreferenceTabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.number.isRequired,
	value: PropTypes.number.isRequired,
};

const ScenarioPreference = (props) => {
	const renderCount = useRef(0);
	renderCount.current++;

	const pageMode = props.mode;

	// Local copy of the current scenario (driven from props)
	const [currScenario, setCurrScenario] = useState(props.item);
	const [prefs, setPrefs] = useState(props.item.prefCategory);
	const [tab, setTab] = useState(String(props.item?.prefCategory?.[0]?.id || ""));

	useEffect(() => {
		debugLog(2, "[ScenarioPreference][FLOW] mounted", {});
	}, []);

	useUpdateEffect(() => {
		setCurrScenario(props.item);
		setPrefs(props.item.prefCategory);

		debugLog(3, "[ScenarioPreference][STATE] props.item changed", {
			propsItem: props.item,
		});
	}, [props.item]);

	const tabChangeHandler = (newValue) => {
		debugLog(3, "[ScenarioPreference][ACTION] tabChangeHandler", { newValue });
		
		const extracted = typeof newValue === "object" && newValue?.value ? newValue.value : newValue;
		setTab(String(extracted));
	};

	const sliderValueChangeHandler = (cfg) => {
		currScenario.scenario_configs = currScenario.scenario_configs.filter((x) => {
			return x.id !== cfg.id;
		});
		currScenario.scenario_configs.push(cfg);

		const updateData = {
			...currScenario,
			updatedAt: new Date(),
			updatedBy: Session().currUser.id,
		};

		debugLog(3, "[ScenarioPreference][ACTION] sliderValueChangeHandler", {
			changedConfig: cfg,
			updateData: updateData,
		});

		setCurrScenario(updateData);
		props.onLocalScenarioChange(updateData);

		props.onClearComplete();
	};

	// --- Logging: Render and State Snapshots ---
	// debugLog(2, "\n↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓↓\n");
	debugLog(9, "[ScenarioPreference][FLOW] render", {
		renderCount: renderCount.current,
		props: props,
	});
	debugLog(9, "[ScenarioPreference][FLOW] renderState", { prefs: prefs });
	// debugLog(4, "\n↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑↑\n");

	return (
		<div data-testid="ScenarioSelections">
			<Box w="100%">
				<Tabs.Root
					key={tab}
					value={tab}
					onValueChange={(val) => tabChangeHandler(val)}
					variant="plain"
				>
					<Tabs.List
						w="100%"
						display="flex"
					>
						{prefs &&
							prefs.map((x, index) => (
								<Tabs.Trigger
									key={index}
									value={String(x.id)}
									flex="1"
									display="flex"
									justifyContent="center"
									alignItems="center"
									textAlign="center"
									fontWeight={600}
									fontSize="1rem"
								>
									{x.name}
								</Tabs.Trigger>
							))}

						<Tabs.Indicator
							mt={50} // moves the indicator DOWN
							height={1} // optional: thicker blue underline
							bg={mColor.NavyBlue}
						/>
					</Tabs.List>

					{prefs &&
						prefs.map((x, index) => (
							<Tabs.Content key={index} value={String(x.id)}>
								<Box my="10px" p="5px">
									<Text fontSize="0.8rem" color={mColor.StonyBlue}>
										{prefInstructions[x.id]}
									</Text>
								</Box>

								<Box w="100%" display="flex" flexWrap="wrap" justifyContent="center" gap="1%">
									{prefs[index].items &&
										prefs[index].items.map((y, idx) => {
											return (
												<Box
													key={idx}
													mb="20px"
													flex="0 0 19%"
													display="flex"
													flexDirection="column"
													boxShadow="0 2px 8px #eee"
													p="20px 0px"
												>
													{prefs[index].id !== 611 && (
														<Box flex="1 1 10%">
															<ChooseTwoValues
																key={idx}
																label={
																	x.id === 601
																		? y.name || y.comp_value
																		: y.comp_value
																}
																range={
																	x.id !== 611 ? "normalized" : "perItem"
																}
																extra={{
																	...y,
																	scenario_id: currScenario.id,
																	decimalPoint:
																		currScenario.baseline
																			.currency_display_decimal,
																	currencyUnit:
																		currScenario.baseline.currency_unit,
																	currencySymbol:
																		currScenario.baseline.currency_symbol,
																}}
																step={1}
																mode={pageMode}
																scenarioConfigs={
																	currScenario.scenario_configs
																}
																pref={x}
																onChange={sliderValueChangeHandler}
															/>
														</Box>
													)}
													{prefs[index].id === 611 && (
														<Box flex="1 1 10%">
															<ChooseOneValue
																key={idx}
																label={
																	x.id === 601
																		? y.name || y.comp_value
																		: y.comp_value
																}
																range={
																	x.id !== 611 ? "normalized" : "perItem"
																}
																extra={{
																	...y,
																	scenario_id: currScenario.id,
																	decimalPoint:
																		currScenario.baseline
																			.currency_display_decimal,
																	currencyUnit:
																		currScenario.baseline.currency_unit,
																	currencySymbol:
																		currScenario.baseline.currency_symbol,
																}}
																step={1}
																mode={pageMode}
																scenarioConfigs={
																	currScenario.scenario_configs
																}
																pref={x}
																onChange={sliderValueChangeHandler}
																onError={props.onError}
															/>
														</Box>
													)}
												</Box>
											);
										})}
								</Box>
							</Tabs.Content>
						))}
				</Tabs.Root>
			</Box>
		</div>
	);
};

export default ScenarioPreference;
